import { Box, Typography, useTheme } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../store/slices/authSlice';
import { getUserRole } from '../util';
import { useGetFinanceTrialsQuery, useUpdateDashboardPreferenceMutation } from '../../store/slices/apiSlice';
import CustomButton from '../../components/@extended/CustomButton';
import SettingsIcon from '@mui/icons-material/Settings';
import DragContainer2 from '../ReportingDashboard/DragContainer2';
import _ from 'lodash';
import FinanceWidgetSetting from './FinanceWidgetSetting';
import TransactionCounts from './widgets/TransactionCounts';
import OverallPaymentStatus from './widgets/OverallPaymentStatus';
import InvoiceCounts from './widgets/InvoiceCounts';
import SiteExpenditure from './widgets/SiteExpenditure';

const FinanceReportingDashboard = () => {
  const user = useSelector(selectCurrentUser);
  const userRole = getUserRole(user);

  const sponsorDetails = useSelector((state) => state.userDetails.user);
  const userPreferences = useSelector((state) => state?.userDetails?.loggedInUser?.dashboardPreferences);

  const theme = useTheme();
  const isSponsor = userRole === 'sponsor';
  const [selectedWidget, setSelectedWidget] = useState(null);
  const [selectedSites, setSelectedSites] = useState([]);
  const [trial, setTrial] = useState('');
  const [siteList, setSiteList] = useState([]);
  const [openSetting, setOpenSetting] = useState(false);
  const [widgetList, setWidgetList] = useState({});

  const [updateDashboardPreference] = useUpdateDashboardPreferenceMutation();
  const { data: financeTrials } = useGetFinanceTrialsQuery(
    {
      sponsorEntityId: sponsorDetails?.financeDetail?.entityId,
      skipStatus: 'Drafted,PendingApproval,Archived',
      page: 1,
      limit: 20,
    },
    {
      skip: !sponsorDetails?.financeDetail?.entityId,
      refetchOnMountOrArgChange: true,
    }
  );

  const getContentByType = (type, additionalProps) => {
    const componentMap = {
      FINANCE_TRIAL_TRANSACTION_COUNT: TransactionCounts,
      FINANCE_TRIAL_TRANSACTION_COUNT_OVERVIEW: OverallPaymentStatus,
      FINANCE_INVOICE_STATUS_COUNT: InvoiceCounts,
      FINANCE_STUDY_BUDGET_OVERVIEW: SiteExpenditure,
    };

    const Component = componentMap[type];
    if (Component) {
      return (
        <Component
          clinicalStudyId={trial}
          studySiteIds={selectedSites}
          siteList={siteList}
          preference={type}
          maximizeWidget={(_widget) => setSelectedWidget(_widget)}
          {...additionalProps}
        />
      );
    } else {
      return null;
    }
  };

  useEffect(() => {
    if (financeTrials) {
      if (!trial) {
        const initialTrial = financeTrials?.data[0];
        setTrial(initialTrial?.id);
        const sites = initialTrial?.clinicalSites.map((site) => {
          const addInfo = JSON.parse(site?.siteEntity?.addditionalInfo);
          return {
            siteName: addInfo?.name,
            id: site?.siteEntity?.id,
            siteTrialId: site?.id,
          };
        });
        if (sites?.length) {
          setSiteList(sites);
          setSelectedSites([sites[0]?.id]);
        }
      } else {
        const initialTrial = financeTrials?.data.find((_trial) => _trial.id === trial);
        const sites = initialTrial?.clinicalSites.map((site) => {
          const addInfo = JSON.parse(site?.siteEntity?.addditionalInfo);
          return {
            siteName: addInfo?.name,
            id: site?.id,
            // siteTrialId: site?.id,
          };
        });
        if (sites?.length) {
          setSiteList(sites);
          setSelectedSites([sites[0]?.id]);
        }
      }
    }
  }, [financeTrials, trial]);

  useEffect(() => {
    if (userPreferences && trial) {
      const filteredPreferences = userPreferences.filter((user) => getContentByType(user.preference.type));

      const updatedWidgetList = {};
      _.orderBy(filteredPreferences, (preference) => preference?.order).forEach((preference) => {
        const content = getContentByType(preference.preference.type, { trialId: trial, siteId: selectedSites });
        updatedWidgetList[preference.preference.type] = {
          preferenceId: preference.id,
          id: preference.preference.type,
          label: preference.preference.label,
          content: content,
          display: preference.visibility,
          order: preference.order,
        };
      });
      setWidgetList(updatedWidgetList);
    }
  }, [userPreferences, trial, selectedSites]);

  const openWidgetSetting = () => {
    setOpenSetting(true);
  };

  const onCloseSetting = () => {
    setOpenSetting(false);
  };

  return (
    <>
      <Box sx={{ backgroundColor: '#FAFAFA' }}>
        {!selectedWidget && (
          <>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2, alignItems: 'center' }}>
              <Typography variant="h6" color="initial">
                Reporting Dashboard
              </Typography>
              <Box>
                <CustomButton startIcon={<SettingsIcon />} variant="outlined" onClick={openWidgetSetting}>
                  Configure
                </CustomButton>
              </Box>
            </Box>
            <DragContainer2
              userPreferences={userPreferences}
              widgets={widgetList}
              maximizeWidget={(_widget) => setSelectedWidget(_widget)}
            />
          </>
        )}
        <FinanceWidgetSetting
          open={openSetting}
          onClose={onCloseSetting}
          trial={trial}
          siteList={siteList}
          selectedSites={selectedSites}
          setTrial={setTrial}
          setSelectedSites={setSelectedSites}
          financeTrials={financeTrials?.data}
        />
      </Box>
    </>
  );
};

export default FinanceReportingDashboard;
